import { Link } from 'react-router-dom'
import { ArrowRight, ArrowLeft, LogIn, Plus, Music, Sparkles, Play, Folder, FilmIcon, Bot, Axe, BookOpenCheck, Pause, Check, Maximize2, X } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/QuickStartPage.stylex'
import { useState, useRef, useEffect } from 'react'

interface QuickStartStepProps {
    title: string
    description?: string
    descriptionParts?: (string | React.ReactNode)[]
    icon: React.ReactNode
    imageUrl: string[]
    actionText: string
    actionHref?: string
    features: string[]
    className?: any // Updated to accept stylex objects
    stepNumber: number
}

function QuickStartStep({
    title,
    description,
    descriptionParts,
    imageUrl,
    actionText,
    actionHref,
    features,
    stepNumber,
    className
}: QuickStartStepProps) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [fullscreenImage, setFullscreenImage] = useState<number | null>(null)
    const [isVideoPlaying, setIsVideoPlaying] = useState<{ [key: string | number]: boolean }>({})
    const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(null)
    const videoRefs = useRef<{ [key: string | number]: HTMLVideoElement | null }>({})

    const isVideo = (url: string) => {
        const lower = url.toLowerCase()
        return lower.endsWith('.mp4') || lower.endsWith('.webm')
    }

    useEffect(() => {
        if (imageUrl.length <= 1) return () => { }

        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % imageUrl.length)
        }, 5000)

        return () => clearInterval(interval)
    }, [imageUrl.length])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && fullscreenImage !== null) {
                setFullscreenImage(null)
            }
        }
        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [fullscreenImage])

    const toggleVideo = (index: number) => {
        const video = videoRefs.current[index]
        if (!video) return

        if (video.paused) {
            video.play()
            setIsVideoPlaying(prev => ({ ...prev, [index]: true }))
        } else {
            video.pause()
            setIsVideoPlaying(prev => ({ ...prev, [index]: false }))
        }
    }

    const toggleFullscreenVideo = () => {
        const video = videoRefs.current['fullscreen']
        if (!video) return

        if (video.paused) {
            video.play()
            setIsVideoPlaying(prev => ({ ...prev, 'fullscreen': true }))
        } else {
            video.pause()
            setIsVideoPlaying(prev => ({ ...prev, 'fullscreen': false }))
        }
    }

    const openFullscreen = (index: number) => {
        setFullscreenImage(index)
        setTimeout(() => {
            const video = videoRefs.current['fullscreen']
            if (video && isVideo(imageUrl[index])) {
                video.play()
                setIsVideoPlaying(prev => ({ ...prev, 'fullscreen': true }))
            }
        }, 100)
    }

    const closeFullscreen = () => {
        setFullscreenImage(null)
    }

    return (
        <>
            <div {...stylex.props(styles.stepRow)}>
                <div {...stylex.props(styles.stepHeader)}>
                    <div {...stylex.props(styles.stepNumber)}>
                        <span {...stylex.props(styles.numberText)}>{stepNumber}</span>
                    </div>
                    <h3 {...stylex.props(styles.stepTitle)}>{title}</h3>
                </div>

                <p {...stylex.props(styles.stepDescription)}>
                    {descriptionParts ? descriptionParts : description}
                </p>

                <div {...stylex.props(styles.stepFeatures)}>
                    {features.map((feature, index) => (
                        <div key={index} {...stylex.props(styles.featureItem)}>
                            <div {...stylex.props(styles.featureCheck)}>
                                <Check size={14} />
                            </div>
                            <span>{feature}</span>
                        </div>
                    ))}
                </div>

                <div {...stylex.props(className || styles.stepImageGallery)}>
                    {imageUrl.map((image, index) => (
                        <div
                            key={index}
                            {...stylex.props(
                                styles.stepImageContainer,
                                index === currentImageIndex ? styles.activeImage : styles.inactiveImage
                            )}
                        >
                            <div
                                {...stylex.props(styles.imageWrapper)}
                                onMouseEnter={() => setHoveredImageIndex(index)}
                                onMouseLeave={() => setHoveredImageIndex(null)}
                            >
                                {isVideo(image) ? (
                                    <video
                                        ref={el => { videoRefs.current[index] = el }}
                                        src={image}
                                        loop
                                        muted
                                        playsInline
                                        autoPlay
                                        onPlay={() => setIsVideoPlaying(prev => ({ ...prev, [index]: true }))}
                                        onPause={() => setIsVideoPlaying(prev => ({ ...prev, [index]: false }))}
                                        {...stylex.props(styles.imageMedia)}
                                    />
                                ) : (
                                    <img
                                        src={image}
                                        alt={`Step ${stepNumber} - Image ${index + 1}`}
                                        {...stylex.props(styles.imageMedia)}
                                    />
                                )}

                                <div {...stylex.props(
                                    styles.imageControls,
                                    hoveredImageIndex === index && styles.controlIconVisible
                                )}>
                                    {isVideo(image) && (
                                        <div
                                            {...stylex.props(styles.gifControl)}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                toggleVideo(index)
                                            }}
                                        >
                                            {!isVideoPlaying[index] ? (
                                                <Play size={24} {...stylex.props(styles.playIcon)} />
                                            ) : (
                                                <Pause size={24} {...stylex.props(styles.pauseIcon)} />
                                            )}
                                        </div>
                                    )}
                                    <div
                                        {...stylex.props(styles.fullscreenControl)}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openFullscreen(index)
                                        }}
                                    >
                                        <Maximize2 size={24} {...stylex.props(styles.fullscreenIcon)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {actionHref && (
                    <Link to={actionHref} {...stylex.props(styles.stepAction)}>
                        {actionText}
                        <ArrowRight size={16} {...stylex.props(styles.actionIcon)} />
                    </Link>
                )}
            </div>

            {fullscreenImage !== null && (
                <div {...stylex.props(styles.fullscreenModal)} onClick={closeFullscreen}>
                    <button {...stylex.props(styles.closeFullscreen)} onClick={closeFullscreen}>
                        <X size={32} />
                    </button>
                    <div {...stylex.props(styles.fullscreenContent)} onClick={(e) => e.stopPropagation()}>
                        <div {...stylex.props(styles.fullscreenMediaWrapper)}>
                            {isVideo(imageUrl[fullscreenImage]) ? (
                                <>
                                    <video
                                        ref={el => { videoRefs.current['fullscreen'] = el }}
                                        src={imageUrl[fullscreenImage]}
                                        loop
                                        playsInline
                                        onPlay={() => setIsVideoPlaying(prev => ({ ...prev, 'fullscreen': true }))}
                                        onPause={() => setIsVideoPlaying(prev => ({ ...prev, 'fullscreen': false }))}
                                        controls={false}
                                        {...stylex.props(styles.fullscreenImage)}
                                    />
                                    <div {...stylex.props(styles.fullscreenControls)}>
                                        <button
                                            {...stylex.props(styles.fullscreenControlButton)}
                                            onClick={toggleFullscreenVideo}
                                        >
                                            {isVideoPlaying['fullscreen'] ? (
                                                <><Pause size={20} /><span>Pause</span></>
                                            ) : (
                                                <><Play size={20} /><span>Play</span></>
                                            )}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <img
                                    src={imageUrl[fullscreenImage]}
                                    alt={`Step ${stepNumber} - Fullscreen`}
                                    {...stylex.props(styles.fullscreenImage)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default function QuickStartPage() {
    const quickStartSteps = [
        {
            title: "Login to Your Account",
            description: "Once logged in, you can access all AiBrush Studio features and start building your first project.",
            icon: <LogIn size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE3SJMK42DTP55TB41WDPZE.mp4"],
            actionText: "Go to Login",
            actionHref: "https://aibrush.co",
            features: [
                "Secure authentication",
                "Remember your preferences",
                "Access your projects and credits"
            ],
            className: styles.stepImageGallery
        },
        {
            title: "From Idea to Creation",
            description: "Have an idea in mind? Just describe your project, and AI will handle the visuals, turning your idea into creative outputs effortlessly.",
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE3Y5VDAYDBVT2XB5G8Z25D.mp4"],
            actionText: "Start Creating",
            actionHref: "https://aibrush.co/projects",
            features: [
                "Get creative media",
                "Turn your idea into reality",
                "Create your own media"
            ],
            className: styles.stepImage
        },
        {
            title: "Create Your First Project by Chatting with AI",
            description: "No idea? No worries! Let AI assistant imagine one for you — it'll write your project's story, build its concept, and set everything up for you.",
            icon: <Plus size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE3ZKF86H8JJKZAY2FRPS6V.mp4"],
            actionText: "Start AI Chat",
            actionHref: "https://aibrush.co/projects",
            features: [
                "AI-guided project setup",
                "Natural language project creation",
                "Smart organization suggestions"
            ],
            className: styles.stepImage
        },
        {
            title: "Enhance & Edit Using our AI models",
            descriptionParts: [
                "Create ",
                <strong key="images">Images</strong>,
                ". Simply describe what you want to create, and AI will help you bring your ideas to life."
            ],
            icon: <Sparkles size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE407MS6NHXRWM0YRGSTS42.mp4"],
            actionText: "Start Creating",
            features: [
                "Create any media type by just describing it",
                "Edit your images and remove anything from it"
            ],
            className: styles.stepImageGallery
        },
        {
            title: "Bring your videos to life",
            descriptionParts: [
                "Create ",
                <strong key="videos">Videos</strong>,
                ", ",
                <strong key="images">Images</strong>,
                ", ",
                <strong key="speech">Speech</strong>,
                ", ",
                <strong key="sound-effects">Sound Effects</strong>,
                ", and ",
                <strong key="music">Music</strong>,
                ". Simply describe what you want to create, and AI will help you bring your ideas to life across all media types."
            ],
            icon: <Sparkles size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE40WKJ5MT0J16TBP61HM2M.mp4"],
            actionText: "Start Creating",
            features: [
                "Turn your words into any type of media",
                "Use references to guide your videos"
            ],
            className: styles.stepImageGallery
        },
        {
            title: "Manage Your Media Library",
            descriptionParts: [
                "From here you can ",
                <strong key="Duplicate">Duplicate</strong>,
                ", or ",
                <strong key="Delete">Delete</strong>,
                " any media type you've created. Organize your AI-generated content and make adjustments to ",
                <strong key="videos">Videos</strong>,
                ", ",
                <strong key="images">Images</strong>,
                ", ",
                <strong key="speech">Speech</strong>,
                ", ",
                <strong key="sound-effects">Sound Effects</strong>,
                ", and ",
                <strong key="music">Music</strong>,
                "."
            ],
            icon: <Folder size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE419KHTPCQBHEDT0XCJS4E.mp4"],
            actionText: "Manage Media",
            features: [
                "Duplicate and remix content",
                "Organize your media library"
            ],
            className: styles.stepImage
        },
        {
            title: "Edit Your Media",
            descriptionParts: [
                "From here you can ",
                <strong key="Edit">Edit</strong>,
                " and make adjustments to ",
                <strong key="videos">Videos</strong>,
                ", ",
                <strong key="images">Images</strong>,
                ", ",
                <strong key="speech">Speech</strong>,
                ", ",
                <strong key="sound-effects">Sound Effects</strong>,
                ", and ",
                <strong key="music">Music</strong>,
                "."
            ],
            icon: <Folder size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE41QS77JCCZ7WDXR8ASD7H.mp4"],
            actionText: "Manage Media",
            features: [
                "Edit existing media files",
                "Duplicate and remix content",
                "Organize your media library"
            ],
            className: styles.stepImage
        },
        {
            title: "Generate Media & Add to Timeline",
            descriptionParts: [
                "Once you've generated your Media, you'll see three key buttons to ",
                <strong key="start">start generating</strong>,
                " new media, ",
                <strong key="regenerate-action">regenerate</strong>,
                " if you want a different version, and ",
                <strong key="add">add the item to your timeline</strong>,
                " for your project. Perfect for content creators and musicians."
            ],
            icon: <Music size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9SEQ5SSCVE51DY6PFA6T4FF/videos/01KAE42ADE2SFPGA8S3M467FVR.mp4"],
            actionText: "Start Creating",
            features: [
                "Generate Media",
                "Regenerate Media",
                "Add to Timeline"
            ],
            className: styles.stepImageGallery
        },
        {
            title: "Timeline",
            descriptionParts: [
                "Once you've generated your Media, you can add it to your timeline for your project."
            ],
            icon: <Music size={24} />,
            imageUrl: ["https://cdn.aibrush.co/01K9KYXE13P9VXYZ3AJFEDB90M/videos/01KAM5XKTNE6GK2ZNANDB9P2ED.mp4"],
            actionText: "Start Creating",
            features: [
                "can edit your timeline",
                "export results"
            ],
            className: styles.stepImageGallery
        }
    ]

    const additionalFeatures = [
        {
            title: "Explore Examples",
            description: "Explore Full Projects Examples created with AiBrush Studio.",
            icon: <BookOpenCheck size={20} />,
            href: "/examples"
        },
        {
            title: "Workspace",
            description: "Explore the workspace and learn how to use it to create any media type.",
            icon: <Axe size={20} />,
            href: "/workspace"
        },
        {
            title: "Timeline Editor",
            description: "Explore the timeline editor and learn how to create and edit videos.",
            icon: <FilmIcon size={20} />,
            href: "/timeline"
        },
        {
            title: "Explore AI Models",
            description: "Discover all available AI models and their capabilities.",
            icon: <Bot size={20} />,
            href: "/ai-models"
        }
    ]

    return (
        <div {...stylex.props(styles.quickStartPage)}>
            <div {...stylex.props(styles.animatedBackground)}>
                <div {...stylex.props(styles.star, styles.star1)}></div>
                <div {...stylex.props(styles.star, styles.star2)}></div>
                <div {...stylex.props(styles.star, styles.star3)}></div>
                <div {...stylex.props(styles.star, styles.star4)}></div>
                <div {...stylex.props(styles.star, styles.star5)}></div>
            </div>

            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            <div {...stylex.props(styles.pageHeader)}>
                <h1 {...stylex.props(styles.pageTitle)}>
                    Begin your <span {...stylex.props(styles.gradientText)}>AiBrush</span> journey
                </h1>
            </div>

            <section {...stylex.props(styles.quickStartSection)}>
                <div {...stylex.props(styles.stepsContainer)}>
                    {quickStartSteps.map((step, index) => (
                        <QuickStartStep
                            key={index}
                            icon={step.icon}
                            {...step}
                            stepNumber={index + 1}
                        />
                    ))}
                </div>
            </section>

            <section {...stylex.props(styles.additionalFeaturesSection)}>
                <div {...stylex.props(styles.featuresGrid)}>
                    {additionalFeatures.map((feature, index) => (
                        <Link key={index} to={feature.href} {...stylex.props(styles.featureCard)}>
                            <div {...stylex.props(styles.featureIcon)}>{feature.icon}</div>
                            <div {...stylex.props(styles.featureContent)}>
                                <h3 {...stylex.props(styles.featureTitle)}>{feature.title}</h3>
                                <p {...stylex.props(styles.featureDescription)}>{feature.description}</p>
                            </div>
                            <ArrowRight size={16} {...stylex.props(styles.featureArrow)} />
                        </Link>
                    ))}
                </div>
            </section>

            <section {...stylex.props(styles.ctaSection)}>
                <div {...stylex.props(styles.ctaContent)}>
                    <h2 {...stylex.props(styles.ctaTitle)}>Ready to Start Creating?</h2>
                    <p {...stylex.props(styles.ctaDescription)}>
                        Jump into your projects and begin your AI-powered creative journey today.
                    </p>
                    <div {...stylex.props(styles.ctaButtons)}>
                        <Link to="https://aibrush.co/projects" {...stylex.props(styles.primaryButton)}>
                            Go to Your Project
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}